import { OMS_TENANT, REACT_APP_OMS_URL } from "constants/constants";
import { normalizeTenantIdList } from "helpers/couponScopeOptions";
import { throwIfFetchNotOk } from "helpers/authenticationErrors";
import {
  getApiErrorMessage,
  parseFetchResponseBody,
} from "helpers/packageManagementApi";
import { tokenUtils } from "utils";

const OMS_TENANT_PACKAGES_PATH =
  "/api/tenant_pricing/get_packages_by_gateway_tenant_ids";

const buildUrl = (path) => {
  const baseUrl = REACT_APP_OMS_URL.replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return new URL(normalizedPath, `${baseUrl}/`);
};

const getHeaders = (token) => {
  if (!token) {
    throw new Error("You must be logged in to call OMS APIs.");
  }

  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-tenant": OMS_TENANT,
    Authorization: `Bearer ${token}`,
  };
};

export const mergePackagesByPackageId = (packageLists) => {
  const packagesById = new Map();

  packageLists.flat().forEach((packageEntry) => {
    if (packageEntry && !packagesById.has(packageEntry.packageId)) {
      packagesById.set(packageEntry.packageId, packageEntry);
    }
  });

  return Array.from(packagesById.values());
};

const toBasicPackageEntry = (packageEntry) => ({
  packageId: packageEntry.packageId,
  packageName: packageEntry.packageName,
  displayName: packageEntry.displayName,
});

const groupPackagesByGatewayTenants = (packages, gatewayTenantIds) => {
  const grouped = Object.fromEntries(
    gatewayTenantIds.map((tenantId) => [String(tenantId), []])
  );

  packages.forEach((packageEntry) => {
    const basicPackage = toBasicPackageEntry(packageEntry);
    const subscriptionTenants = Array.isArray(packageEntry.subscriptionTenants)
      ? packageEntry.subscriptionTenants
      : [];

    subscriptionTenants.forEach((tenantId) => {
      const tenantKey = String(tenantId);
      if (!grouped[tenantKey]) {
        grouped[tenantKey] = [];
      }
      grouped[tenantKey].push(basicPackage);
    });
  });

  return grouped;
};

const normalizePackagesByGatewayTenants = (data, gatewayTenantIds) => {
  if (!data || typeof data !== "object") {
    return {};
  }

  if (Array.isArray(data)) {
    return groupPackagesByGatewayTenants(data, gatewayTenantIds);
  }

  return data;
};

const requestPackagesByGatewayTenants = async (gatewayTenantIds = []) => {
  const normalizedGatewayTenantIds = normalizeTenantIdList(gatewayTenantIds);

  if (normalizedGatewayTenantIds.length === 0) {
    return {};
  }

  if (!REACT_APP_OMS_URL) {
    throw new Error("OMS API URL is not configured.");
  }

  const response = await fetch(buildUrl(OMS_TENANT_PACKAGES_PATH), {
    method: "POST",
    headers: getHeaders(tokenUtils.getToken()),
    body: JSON.stringify({
      gatewayTenantIds: normalizedGatewayTenantIds,
    }),
  });

  const responseBody = await parseFetchResponseBody(response);

  throwIfFetchNotOk(
    response,
    responseBody,
    "Failed to fetch package information from OMS."
  );

  if (responseBody.status !== true) {
    throw new Error(
      getApiErrorMessage(
        responseBody,
        "Failed to fetch package information.",
        response.status
      )
    );
  }

  return normalizePackagesByGatewayTenants(
    responseBody.data,
    normalizedGatewayTenantIds
  );
};

export const fetchPackagesGroupedByGatewayTenants = async (
  gatewayTenantIds = []
) => requestPackagesByGatewayTenants(gatewayTenantIds);

export const fetchPackagesByGatewayTenants = async (gatewayTenantIds = []) => {
  const packagesByTenant = await requestPackagesByGatewayTenants(
    gatewayTenantIds
  );

  return mergePackagesByPackageId(
    Object.values(packagesByTenant).filter(Array.isArray)
  );
};
